import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Stack, CircularProgress, Alert, Tooltip,
  ToggleButton, ToggleButtonGroup, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, Divider,
} from '@mui/material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip as RTooltip,
} from 'recharts';
import { httpClient } from '../services/http-client';
import { chartTooltipProps } from '../components/charts/chartTooltip';
import { formatCurrency } from '../utils/currency';
import { useBaseCurrency } from '../stores/currency-store';
import { useAuthStore } from '../stores/auth-store';

interface DividendRow {
  symbol: string;
  currency: string;
  payments: number;
  received: string;
  trailing_twelve: string;
  projected: string;
  first_paid?: string | null;
  last_paid?: string | null;
}

const num = (s: string | null | undefined) => (s ? parseFloat(s) : 0);

/**
 * Dividend and other investment income.
 *
 * Two numbers per holding, and they are deliberately different things:
 *
 *  - RECEIVED is history. Every distribution ever recorded, summed. It is a
 *    fact, and cancelled dividends (CXLDIV) are subtracted rather than ignored.
 *  - PROJECTED is the trailing twelve months carried forward. It is an
 *    extrapolation and is labelled as one.
 *
 * The projection deliberately does NOT model ex-dividend dates, declared
 * increases or payment schedules — none of that is in the imported data. A
 * position sold last year projects zero rather than repeating history it no
 * longer holds, and a position bought two months ago will UNDERSTATE, because
 * only two months of payments sit inside the trailing window. Both are visible
 * from the payment count and last-paid date, which is why they are shown.
 *
 * Return of capital is excluded upstream: it reduces cost base rather than
 * being income, and counting it here would overstate yield in one direction
 * while understating the eventual capital gain in the other.
 */
export const DividendsPage: React.FC = () => {
  const baseCurrency = useBaseCurrency();
  const currentUserId = useAuthStore((s) => s.user?.id);
  const [scope, setScope] = useState<string>('mine');
  const [rows, setRows] = useState<DividendRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [drill, setDrill] = useState<string | null>(null);
  const [detail, setDetail] = useState<any | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const owner = scope === 'mine' ? currentUserId : scope;
    httpClient
      .get<DividendRow[]>(`/investments/dividends?owner=${encodeURIComponent(owner ?? '')}`)
      .then((res) => { if (!cancelled) setRows(Array.isArray(res) ? res : []); })
      .catch((e: any) => { if (!cancelled) setError(e?.message ?? 'Could not load dividends'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [scope, currentUserId]);

  // Every payment behind the clicked row, scoped to the SAME owner as the
  // summary -- a household total behind a per-member row would not reconcile.
  useEffect(() => {
    if (!drill) { setDetail(null); return; }
    let cancelled = false;
    setDetailLoading(true);
    const owner = scope === 'mine' ? currentUserId : scope;
    httpClient
      .get<any>(`/investments/dividends/${encodeURIComponent(drill)}?owner=${encodeURIComponent(owner ?? '')}`)
      .then((r) => { if (!cancelled) setDetail((r as any)?.data ?? r); })
      .catch(() => { if (!cancelled) setDetail(null); })
      .finally(() => { if (!cancelled) setDetailLoading(false); });
    return () => { cancelled = true; };
  }, [drill, scope, currentUserId]);

  /**
   * Totals are kept PER CURRENCY rather than summed across them. A USD
   * dividend added to a CAD one at face value understates by roughly the
   * exchange rate, and nothing on screen would look wrong.
   */
  const totals = useMemo(() => {
    const map = new Map<string, { received: number; projected: number; payments: number }>();
    rows.forEach((r) => {
      const t = map.get(r.currency) ?? { received: 0, projected: 0, payments: 0 };
      t.received += num(r.received);
      t.projected += num(r.projected);
      t.payments += r.payments;
      map.set(r.currency, t);
    });
    return Array.from(map.entries());
  }, [rows]);

  /**
   * The chart is restricted to ONE currency — whichever carries the most
   * income. Plotting CAD and USD bars against a shared axis would compare
   * them at face value and make a US$500 holding look smaller than a
   * CA$600 one when it is in fact larger. The heading names the currency
   * so the restriction is visible rather than implied.
   */
  const chartCurrency = useMemo<string>(() => {
    let best: string = baseCurrency;
    let bestVal = -1;
    totals.forEach(([ccy, t]) => {
      if (t.received > bestVal) { bestVal = t.received; best = ccy; }
    });
    return best;
  }, [totals, baseCurrency]);

  const chartData = useMemo(
    () => rows
      .filter((r) => r.currency === chartCurrency && num(r.projected) > 0)
      .slice(0, 12)
      .map((r) => ({ symbol: r.symbol, projected: num(r.projected) })),
    [rows, chartCurrency],
  );

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
        <Typography variant="h4" fontWeight={700}>Dividends</Typography>
        <ToggleButtonGroup
          size="small" exclusive value={scope}
          onChange={(_, v) => v && setScope(v)}
        >
          <ToggleButton value="mine">Mine</ToggleButton>
          <ToggleButton value="household">Whole household</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Income recorded from your brokerage imports. Return of capital is excluded —
        it reduces your cost base rather than counting as income.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {rows.length === 0 ? (
        <Alert severity="info">
          No investment income recorded yet. Import a brokerage activity file to populate this.
        </Alert>
      ) : (
        <>
          <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap' }}>
            {totals.map(([ccy, t]) => (
              <Paper key={ccy} sx={{ p: 2, minWidth: 220, flex: '1 1 220px' }}>
                <Typography variant="overline" color="text.secondary">
                  Received ({ccy}) · {t.payments} payments
                </Typography>
                <Typography variant="h5" fontWeight={700}>
                  {formatCurrency(t.received, ccy, { base: baseCurrency })}
                </Typography>
                <Tooltip title="Trailing twelve months, carried forward. An estimate, not a forecast.">
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Projected next year ≈ {formatCurrency(t.projected, ccy, { base: baseCurrency })}
                  </Typography>
                </Tooltip>
              </Paper>
            ))}
          </Stack>

          {chartData.length > 0 && (
            <Paper sx={{ p: 2, mb: 3, height: 280 }}>
              <Typography variant="overline" color="text.secondary">
                Projected annual income by holding · {chartCurrency} only
              </Typography>
              <ResponsiveContainer width="100%" height="88%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="symbol" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} width={70} />
                  <RTooltip
                    {...chartTooltipProps}
                    formatter={(v: any) => v.toFixed(2)}
                  />
                  {/* A literal colour, not a CSS variable: recharts writes the
                      fill into an SVG attribute, where an unresolved var()
                      renders nothing at all and the chart silently comes up
                      blank. */}
                  <Bar
                    dataKey="projected"
                    fill="#4f9cf9"
                    radius={[4, 4, 0, 0]}
                    // Without this the bars grow from zero height on mount and
                    // can stay at zero, leaving axes and gridlines drawn over
                    // an empty plot -- which reads as "no dividends" rather
                    // than "the animation did not run".
                    isAnimationActive={false}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          )}

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Symbol</TableCell>
                  <TableCell align="right">Payments</TableCell>
                  <TableCell align="right">Received (all time)</TableCell>
                  <TableCell align="right">Last 12 months</TableCell>
                  <TableCell align="right">Projected</TableCell>
                  <TableCell>Last paid</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow
                    key={`${r.symbol}-${r.currency}`}
                    hover
                    tabIndex={0}
                    onClick={() => setDrill(r.symbol)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') setDrill(r.symbol);
                    }}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" fontWeight={650}>{r.symbol}</Typography>
                        {r.currency !== baseCurrency && (
                          <Chip label={r.currency} size="small" variant="outlined" sx={{ height: 18 }} />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">{r.payments}</TableCell>
                    <TableCell align="right">
                      {formatCurrency(r.received, r.currency, { base: baseCurrency })}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(r.trailing_twelve, r.currency, { base: baseCurrency })}
                    </TableCell>
                    <TableCell align="right">
                      {num(r.projected) > 0
                        ? formatCurrency(r.projected, r.currency, { base: baseCurrency })
                        : (
                          <Tooltip title="Nothing paid in the last 12 months, so there is no basis to project from.">
                            <Typography variant="body2" color="text.disabled">—</Typography>
                          </Tooltip>
                        )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {r.last_paid ? new Date(r.last_paid).toLocaleDateString() : '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Drill-down: which accounts paid it, and every payment over time.
          A chart plus the ledger behind it -- the chart shows the rhythm
          (are payments steady, growing, missed?) and the table answers
          "which of my accounts did this come from". */}
      <Dialog open={!!drill} onClose={() => setDrill(null)} maxWidth="md" fullWidth>
        <DialogTitle>{drill}</DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : !detail || (detail.payments ?? []).length === 0 ? (
            <Alert severity="info">No payments recorded.</Alert>
          ) : (
            <>
              <Typography variant="overline" color="text.secondary">
                Paid into
              </Typography>
              <Stack spacing={0.75} sx={{ mt: 1, mb: 2 }}>
                {(detail.accounts ?? []).map((a: any, i: number) => (
                  <Stack key={i} direction="row" spacing={1} alignItems="center"
                    sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                    <Typography variant="body2" fontWeight={650}>{a.account}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {a.institution}
                    </Typography>
                    <Chip size="small" variant="outlined" sx={{ height: 18 }} label={a.owner} />
                    <Box sx={{ flex: 1 }} />
                    <Typography variant="caption" color="text.secondary">
                      {a.payments} payments
                    </Typography>
                    <Typography variant="body2" fontWeight={650} sx={{ minWidth: 96, textAlign: 'right' }}>
                      {formatCurrency(a.total, a.currency, { base: baseCurrency })}
                    </Typography>
                  </Stack>
                ))}
              </Stack>

              {(() => {
                /**
                 * Explain a change rather than leaving the user to infer it.
                 *
                 * A falling total has two completely different causes that
                 * look identical in the total: selling shares, and the
                 * company cutting its rate. Separating them is the whole
                 * reason per-share is computed -- BCE.TO here fell from
                 * $2,770 to $394 because the position went 2,777 -> 900 AND
                 * the rate went $0.9975 -> $0.4375. Reading only the total,
                 * you cannot tell which happened, or that both did.
                 */
                const paid = (detail.payments ?? []).filter((p: any) => Number(p.per_share) > 0);
                if (paid.length < 2) return null;
                const latest = paid[0];
                const oldest = paid[paid.length - 1];
                const rateChanged = Number(latest.per_share) !== Number(oldest.per_share);
                const sharesChanged = Number(latest.shares_held) !== Number(oldest.shares_held);
                if (!rateChanged && !sharesChanged) return null;

                const pct = (a: number, b: number) =>
                  b === 0 ? 0 : Math.round(((a - b) / b) * 100);

                return (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 650, mb: 0.5 }}>
                      Why the amount changed
                    </Typography>
                    {sharesChanged && (
                      <Typography variant="body2">
                        Your position went from{' '}
                        {Number(oldest.shares_held).toLocaleString()} to{' '}
                        {Number(latest.shares_held).toLocaleString()} shares
                        {' '}({pct(Number(latest.shares_held), Number(oldest.shares_held))}%).
                      </Typography>
                    )}
                    {rateChanged && (
                      <Typography variant="body2">
                        The dividend itself went from{' '}
                        {formatCurrency(oldest.per_share, oldest.currency, { base: baseCurrency })} to{' '}
                        {formatCurrency(latest.per_share, latest.currency, { base: baseCurrency })} per share
                        {' '}({pct(Number(latest.per_share), Number(oldest.per_share))}%) — a change by the
                        company, not by you.
                      </Typography>
                    )}
                  </Alert>
                );
              })()}

              <Divider sx={{ my: 2 }} />
              <Typography variant="overline" color="text.secondary">
                Payments over time
              </Typography>
              <Box sx={{ height: 200, mt: 1 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={[...(detail.payments ?? [])]
                      .slice()
                      .reverse()
                      .map((p: any) => ({
                        label: new Date(p.date).toLocaleDateString(undefined,
                          { month: 'short', year: '2-digit' }),
                        amount: Number(p.amount),
                      }))}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} width={62} />
                    <RTooltip
                      {...chartTooltipProps}
                      formatter={(v: any) => Number(v).toFixed(2)}
                    />
                    <Bar dataKey="amount" fill="#5ad1a5" radius={[3, 3, 0, 0]}
                      isAnimationActive={false} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>

              <Divider sx={{ my: 2 }} />
              <Typography variant="overline" color="text.secondary">
                Every payment
              </Typography>
              <TableContainer sx={{ maxHeight: 300, mt: 1 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Account</TableCell>
                      <TableCell align="right">Shares</TableCell>
                      <TableCell align="right">Per share</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(detail.payments ?? []).map((p: any, i: number) => (
                      <TableRow key={i} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {new Date(p.date).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <Chip size="small" variant="outlined" sx={{ height: 18 }}
                            label={p.kind} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{p.account}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {p.owner}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {Number(p.shares_held) > 0 ? Number(p.shares_held).toLocaleString() : '—'}
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          {Number(p.per_share) > 0
                            ? formatCurrency(p.per_share, p.currency, { base: baseCurrency })
                            : '—'}
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          {formatCurrency(p.amount, p.currency, { base: baseCurrency })}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setDrill(null)}>Close</Button></DialogActions>
      </Dialog>
    </Box>
  );
};
